import { UpdateUserContext, VibratoClientContext } from "@/components/dashboard/contexts"
import { Buffer } from "buffer"
import { tileGradients } from "@/lib/call-tiles"
import { countryCodeInfo, supportedCountryCodes } from "@/lib/locales"
import { formatAnyPhoneNumber } from "@/lib/phone"
import { settings } from "@/settings"
import { tasks } from "@/task-library"
import { Task } from "@/tasks"
import {
  Book,
  Building as Briefcase,
  Building,
  Building as Business,
  Calendar,
  Car,
  DashboardDots as ChartBar,
  User as ChildCare,
  Settings as Cog,
  CreditCard,
  UserCircle as Elderly,
  Shield as Fire,
  Shield as Gavel,
  Globe,
  Building as Government,
  Gym,
  Heart,
  Laptop,
  LightBulb,
  Heart as MedicalCross,
  Phone,
  Play,
  Search,
  Shield,
  ShieldCheck,
  ShoppingBag,
  Gym as Sports,
  Building as Theater,
  UserCircle,
  Heart as VolunteerActivism
} from "iconoir-react"
import { useContext, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import DemoCallModal from "../DemoCallModal"
import VerifyPhoneNumberModal from "../VerifyPhoneNumberModal"
import { LoadingSpinner } from "../../LoadingSpinner"
import React from "react"

interface CategoryIconProps {
  category: string
  className?: string
}

interface BusinessDetails {
  name: string
  phoneNumber: string
  countryCode: string
}

interface BusinessSearchResult {
  document: {
    id: string
    name: string
    phoneNumber?: string
    phone?: string
    countryCode?: string
    location?: string
    city?: string
    state?: string
    address?: string
  }
}

interface BusinessSearchResponse {
  hits: BusinessSearchResult[]
}

// Add interface for the API response
interface BusinessSearchApiResult {
  id: string
  name: string
  phone: string | null
  country_code: string | null
  city: string | null
  state: string | null
  address: string | null
}

export const CategoryIcon = ({ category, className = "w-5 h-5" }: CategoryIconProps) => {
  switch (category) {
    case "Financial & Bills":
      return <CreditCard className={className} />
    case "Healthcare & Wellness":
      return <Heart className={className} />
    case "Appointments & Scheduling":
      return <Calendar className={className} />
    case "Customer Service":
      return <Phone className={className} />
    case "Vehicle & Transportation":
      return <Car className={className} />
    case "Personal & Social":
      return <UserCircle className={className} />
    case "Business & Work":
      return <Business className={className} />
    case "Real Estate & Property":
      return <Building className={className} />
    case "Education & Training":
      return <Book className={className} />
    case "Shopping & Retail":
      return <ShoppingBag className={className} />
    case "Fitness & Sports":
      return <Gym className={className} />
    case "Travel & Tourism":
      return <Globe className={className} />
    case "Technology & Digital Services":
      return <Laptop className={className} />
    case "Legal & Compliance":
      return <Gavel className={className} />
    case "Insurance & Claims":
      return <Shield className={className} />
    case "Senior Services":
      return <Elderly className={className} />
    case "Emergency & Crisis Services":
      return <Fire className={className} />
    case "Entertainment & Events":
      return <Theater className={className} />
    case "Professional Services":
      return <Briefcase className={className} />
    case "Government & Civic Services":
      return <Government className={className} />
    case "Employment & Career":
      return <Business className={className} />
    case "Travel & Tourism Services":
      return <Globe className={className} />
    case "Professional Development & Career Services":
      return <ChartBar className={className} />
    case "Pet Services & Veterinary Care":
      return <UserCircle className={className} />
    case "Consumer Protection & Advocacy":
      return <ShieldCheck className={className} />
    case "Digital Services & Online Accounts":
      return <Cog className={className} />
    case "Educational Support & Academic Services":
      return <Book className={className} />
    case "Government & Public Services":
      return <Government className={className} />
    case "Legal & Documentation Services":
      return <Gavel className={className} />
    case "Financial Planning & Investment Services":
      return <ChartBar className={className} />
    case "Religious & Community Services":
      return <VolunteerActivism className={className} />
    case "Sports & Recreation Services":
      return <Sports className={className} />
    case "Transportation & Logistics Services":
      return <Car className={className} />
    case "Senior Care & Aging Services":
      return <Elderly className={className} />
    case "Child Care & Family Services":
      return <ChildCare className={className} />
    case "Mental Health & Counseling Services":
      return <MedicalCross className={className} />
    case "Housing & Real Estate Services":
      return <Building className={className} />
    case "Food & Dining Services":
      return <ShoppingBag className={className} />
    case "Fitness & Wellness Services":
      return <Gym className={className} />
    default:
      return <Building className={className} />
  }
}

export default function ChooseTask(): React.JSX.Element {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const taskSlug = searchParams.get("taskSlug")
  const bizDetailsParam = searchParams.get("bizDetails")
  const titleRef = useRef<HTMLHeadingElement>(null)
  const phoneNumberInputRef = useRef<HTMLInputElement>(null)
  const vibratoClient = useContext(VibratoClientContext)
  const setUser = useContext(UpdateUserContext)
  const [showDemoModal, setShowDemoModal] = useState(false)
  const [showVerifyPhoneModal, setShowVerifyPhoneModal] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [businessSearchQuery, setBusinessSearchQuery] = useState("")
  const [activeSearchQuery, setActiveSearchQuery] = useState("")
  const [searchLocation, setSearchLocation] = useState("San Francisco, CA")
  const [searchResults, setSearchResults] = useState<BusinessSearchResponse | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const searchResultsRef = useRef<HTMLDivElement>(null)
  const taskResultsRef = useRef<HTMLDivElement>(null)
  const [displayedTasks, setDisplayedTasks] = useState<Task[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [selectedTask, setSelectedTask] = useState<Task | null>(null)
  const [showAllCategories, setShowAllCategories] = useState(false)
  const [businessDetails, setBusinessDetails] = useState<BusinessDetails>({
    name: "",
    phoneNumber: "",
    countryCode: "1"
  })
  const [phoneError, setPhoneError] = useState<string | null>(null)
  const [currentStep, setCurrentStep] = useState<"select-task" | "business-details">("select-task")
  const perfomedInitialSlideNaviation = useRef(false)
  const [businessNameError, setBusinessNameError] = useState<string | null>(null)
  const [isTaskSearching, setIsTaskSearching] = useState(false)

  // Get gradient for a task based on its index
  const getTaskGradient = (index: number) => tileGradients[index % tileGradients.length]

  // Featured categories that will be shown initially
  const featuredCategories = settings.SHOW_NATIVE_APP
    ? ["Customer Service", "Appointments & Scheduling", "Healthcare & Wellness", "Financial & Bills", "Business & Work"]
    : [
        "Appointments & Scheduling",
        "Customer Service",
        "Education & Training",
        "Financial & Bills",
        "Healthcare & Wellness",
        "Insurance & Claims",
        "Legal & Compliance",
        "Real Estate & Property",
        "Technology & Digital Services",
        "Transportation & Logistics Services"
      ]

  // Get all available categories from tasks object, sorted alphabetically
  const allCategories = useMemo(() => {
    const categories = [...new Set(Object.keys(tasks))]
    return categories.sort()
  }, [])

  // Determine which categories to show based on showAllCategories state
  const displayedCategories = showAllCategories ? allCategories : featuredCategories

  useEffect(() => {
    document.title = "Vibrato - Explore"
    window.scrollTo(0, 0)
  }, [])

  // Load initial tasks and filter by category
  useEffect(() => {
    const loadInitialTasks = () => {
      // Only load from task library if we're not in a search state
      if (activeSearchQuery.trim() !== "" || hasSearched) {
        return
      }

      // Use task library for initial loading and category filtering
      const allTasks = Object.entries(tasks).flatMap(([category, categoryTasks]) =>
        categoryTasks.map((task) => {
          task.category = category
          return task
        })
      )

      let combinedTasks = allTasks
      // Filter by category if one is selected
      if (selectedCategory) {
        combinedTasks = combinedTasks.filter((task) => task.category === selectedCategory)
      }

      setDisplayedTasks(combinedTasks)
    }

    loadInitialTasks()
  }, [selectedCategory, activeSearchQuery, hasSearched])

  // Add validation function before the return statement
  const validatePhoneNumber = (phoneNumber: string, countryCode: string): boolean => {
    // Remove all non-numeric characters
    const sanitizedPhone = phoneNumber.replace(/\D/g, "")
    const sanitizedCountry = countryCode.replace(/\D/g, "")

    // Basic validation - ensure phone number is between 7 and 15 digits
    // and country code is between 1 and 3 digits
    if (sanitizedPhone.length < 7 || sanitizedPhone.length > 15) {
      setPhoneError("Phone number must be between 7 and 15 digits")
      return false
    }

    if (sanitizedCountry.length < 1 || sanitizedCountry.length > 3) {
      setPhoneError("Invalid country code")
      return false
    }

    // Clear any existing error
    setPhoneError(null)
    return true
  }

  // Add validation function for business name
  const validateBusinessName = (name: string): boolean => {
    if (!name.trim()) {
      setBusinessNameError("Business name is required")
      return false
    }
    if (name.trim() === "") {
      setBusinessNameError("Business name cannot be empty")
      return false
    }

    console.log("Business name is valid")

    // Clear any existing error
    setBusinessNameError(null)
    return true
  }

  // Handle task selection
  useEffect(() => {
    if (!taskSlug) return

    const allTasks = Object.entries(tasks).flatMap(([category, categoryTasks]) =>
      categoryTasks.map((task) => {
        task.category = category
        return task
      })
    )

    const taskToSelect = allTasks.find((task) => task.id === taskSlug)
    if (taskToSelect) {
      setSelectedTask(taskToSelect)
      setSelectedCategory(taskToSelect.category || null)
    }
  }, [taskSlug])

  // Center selected task on screen when task is selected
  useEffect(() => {
    if (selectedTask) {
      // Wait for the animation/rendering to complete before scrolling
      setTimeout(() => {
        console.log("Scrolling to selected task", selectedTask.id)
        // Find the selected task element by its unique identifier
        const selectedElement = document.querySelector(`[data-task-id="${selectedTask.id}"]`)
        if (selectedElement) {
          selectedElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest"
          })
        }
      }, 100)
    }
  }, [selectedTask])

  // Handle moving to business details step
  useEffect(() => {
    if (!bizDetailsParam || !selectedTask || perfomedInitialSlideNaviation.current) return

    setCurrentStep("business-details")
    perfomedInitialSlideNaviation.current = true
  }, [bizDetailsParam, selectedTask])

  // Scroll to title when switching to business details
  useEffect(() => {
    if (currentStep === "business-details" && titleRef.current) {
      titleRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }, [currentStep])

  // Replace the useEffect search with a direct search function
  const handleSearch = async () => {
    if (!businessSearchQuery.trim()) {
      setSearchResults(null)
      return
    }

    setIsSearching(true)
    try {
      const response = await vibratoClient.searchBusinesses({
        query: businessSearchQuery,
        location: searchLocation,
        page: 1,
        page_size: 10
      })

      const transformedResponse: BusinessSearchResponse = {
        hits: response.results.map((result: BusinessSearchApiResult) => ({
          document: {
            id: result.id.toString(),
            name: result.name,
            phone: result.phone || undefined,
            countryCode: result.country_code || undefined,
            city: result.city || undefined,
            state: result.state || undefined,
            address: result.address || undefined
          }
        }))
      }

      setSearchResults(transformedResponse)

      // Scroll to results after a brief delay to ensure rendering
      setTimeout(() => {
        searchResultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
      }, 100)
    } catch (error) {
      console.error("Search error:", error)
    } finally {
      setIsSearching(false)
    }
  }

  // Function to handle task search
  const handleTaskSearch = async () => {
    if (!searchQuery.trim()) {
      // If search query is empty, clear search state
      setHasSearched(false)
      setActiveSearchQuery("")
      return
    }

    setIsTaskSearching(true)
    setHasSearched(true)
    setActiveSearchQuery(searchQuery)

    try {
      // Use search results directly without mapping back to task library
      let searchResults = await vibratoClient.searchFrontendTasks(searchQuery, 20)

      // Filter search results by category if one is selected
      if (selectedCategory) {
        searchResults = searchResults.filter((task: any) => task.category === selectedCategory)
      }

      // Transform search results to Task interface format
      const combinedTasks = searchResults.map((searchResult: any) => ({
        id: searchResult.frontend_id || searchResult.id,
        title: searchResult.title || searchResult.name,
        description: searchResult.description,
        category: searchResult.category,
        generatePrompt: (businessDetails: string) => {
          // Use the search result's prompt template if available, otherwise create a generic one
          if (searchResult.prompt_template) {
            return searchResult.prompt_template.replace("${businessDetails}", businessDetails)
          }
          return `You are an AI assistant making a call on behalf of the user. The user wants to: ${searchResult.title || searchResult.name}.

Your goal is to help the user accomplish this task efficiently and professionally. Introduce yourself as calling on behalf of the user, explain the purpose of the call, and navigate the conversation to achieve the user's goal.

Business details:
${businessDetails}`
        }
      })) as Task[]

      setDisplayedTasks(combinedTasks)

      // Scroll to the task results area after a brief delay to ensure rendering
      setTimeout(() => {
        if (taskResultsRef.current) {
          taskResultsRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
        }
      }, 100)

      console.log("Search results", combinedTasks.length)
    } catch (error) {
      console.error("Error fetching tasks:", error)
      setDisplayedTasks([]) // Clear tasks on error
    } finally {
      setIsTaskSearching(false)
    }
  }

  const handleSearchResultClick = (business: BusinessSearchResult["document"]) => {
    setBusinessDetails({
      name: business.name,
      phoneNumber: business.phone || "",
      countryCode: business.countryCode || "1" // Default to US if not provided
    })
    // Focus on the phone number input after a short delay to ensure the form is rendered
    setTimeout(() => {
      phoneNumberInputRef.current?.focus()
    }, 100)
  }

  // Function to create a dynamic task from search query
  const createDynamicTaskFromSearch = (query?: string) => {
    const searchText = query || searchQuery
    if (!searchText.trim()) return

    // Create a custom task object from the search query
    const customTask: Task = {
      id: `custom-${Date.now()}`,
      title: searchText.trim().charAt(0).toUpperCase() + searchText.trim().slice(1).toLowerCase(),
      description: `Custom task: ${searchText.trim().toLowerCase()}`,
      category: "Custom Task",
      generatePrompt: (businessDetails: string) => {
        return `You are an AI assistant making a call on behalf of the user. The user wants to: ${searchText.trim()}.

Your goal is to help the user accomplish this task efficiently and professionally. Introduce yourself as calling on behalf of the user, explain the purpose of the call, and navigate the conversation to achieve the user's goal.

Business details:
${businessDetails}`
      }
    }

    // Clear the search query so it doesn't populate the business search field
    setSearchQuery("")
    setActiveSearchQuery("")

    // Set the custom task as the selected task and move to business details
    setSelectedTask(customTask)
    setCurrentStep("business-details")

    // Scroll to the title after a brief delay to ensure the view has changed
    setTimeout(() => {
      titleRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    }, 100)
  }

  // Add a function to clear the search
  const clearSearch = () => {
    setSearchQuery("")
    setActiveSearchQuery("")
    setHasSearched(false)

    // Reload initial tasks from task library
    const allTasks = Object.entries(tasks).flatMap(([category, categoryTasks]) =>
      categoryTasks.map((task) => {
        task.category = category
        return task
      })
    )

    let combinedTasks = allTasks
    // Filter by category if one is selected
    if (selectedCategory) {
      combinedTasks = combinedTasks.filter((task) => task.category === selectedCategory)
    }

    setDisplayedTasks(combinedTasks)
  }

  // Add this helper function before the return statement
  const formatAddress = (address?: string, city?: string, state?: string): string | null => {
    const components = [address, city, state].filter((component) => component && component.length > 0)
    return components.length > 0 ? components.join(", ") : null
  }

  return (
    <div className="h-full w-full">
      <DemoCallModal showModal={showDemoModal} setShowModal={setShowDemoModal} />
      <VerifyPhoneNumberModal
        show={showVerifyPhoneModal}
        setShow={setShowVerifyPhoneModal}
        onClose={() => setShowVerifyPhoneModal(false)}
        onPhoneNumberVerified={async () => {
          const session = await vibratoClient.getUserApiSession()
          setUser(session)
        }}
      />

      <div className="flex flex-col h-full w-full">
        <div className="flex flex-col py-8 px-4 sm:px-6 lg:px-8" id="choose-task">
          <div className="text-center space-y-4 mb-12">
            <h1
              ref={titleRef}
              className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
            >
              {currentStep === "business-details"
                ? "Enter Business Details"
                : selectedTask
                  ? selectedTask.title
                  : "What Can Vibrato Help You With Today?"}
            </h1>
            <p className="text-xl text-gray-600">
              {currentStep === "business-details"
                ? "Please provide the business name and phone number you'd like to call."
                : selectedTask
                  ? "Ready to start this task? Click 'Next' below to enter business details, or choose a different task."
                  : "Choose from our most popular tasks or search for something specific. Our AI assistant will handle the call while you focus on what matters."}
            </p>
          </div>

          {currentStep === "select-task" ? (
            <div className="space-y-8">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search tasks by name or description..."
                    className="bg-white w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value)
                      if (e.target.value === "" && !hasSearched) {
                        setHasSearched(false)
                      }
                    }}
                    onKeyDown={(e) => e.key === "Enter" && handleTaskSearch()}
                  />
                  {searchQuery && hasSearched && (
                    <button
                      onClick={clearSearch}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label="Clear search"
                    >
                      ×
                    </button>
                  )}
                </div>
                <div className="w-full md:w-64">
                  <select
                    value={selectedCategory || ""}
                    onChange={(e) => setSelectedCategory(e.target.value || null)}
                    className="bg-white w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                  >
                    <option value="">All Categories</option>
                    {allCategories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={handleTaskSearch}
                  className="min-w-[120px] px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2"
                  disabled={isTaskSearching}
                >
                  {isTaskSearching ? (
                    <>
                      <LoadingSpinner className="w-5 h-5 text-white" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="w-5 h-5" />
                      Search
                    </>
                  )}
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex flex-wrap gap-3 justify-center">
                  <button
                    onClick={async () => {
                      setSelectedCategory(null)
                      setSelectedTask(null)

                      // If we're in a search state, re-run the search with no category filter
                      if (hasSearched && activeSearchQuery.trim() !== "") {
                        await handleTaskSearch()
                      } else {
                        // Otherwise, clear search and reload from task library
                        setHasSearched(false)
                        setActiveSearchQuery("")

                        const allTasks = Object.entries(tasks).flatMap(([category, categoryTasks]) =>
                          categoryTasks.map((task) => {
                            task.category = category
                            return task
                          })
                        )
                        setDisplayedTasks(allTasks)
                      }
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2
                      ${
                        !selectedCategory
                          ? "bg-blue-100 text-blue-800 hover:bg-blue-200 shadow-sm"
                          : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                      }`}
                  >
                    All Categories
                  </button>
                  {displayedCategories.map((category) => (
                    <button
                      key={category}
                      onClick={async () => {
                        setSelectedCategory(category)
                        setSelectedTask(null)

                        // If we're in a search state, re-run the search with the new category filter
                        if (hasSearched && activeSearchQuery.trim() !== "") {
                          await handleTaskSearch()
                        } else {
                          // Otherwise, filter task library by category
                          setHasSearched(false)
                          setActiveSearchQuery("")

                          const allTasks = Object.entries(tasks).flatMap(([cat, categoryTasks]) =>
                            categoryTasks.map((task) => {
                              task.category = cat
                              return task
                            })
                          )
                          const filteredTasks = allTasks.filter((task) => task.category === category)
                          setDisplayedTasks(filteredTasks)
                        }
                      }}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2
                        ${
                          selectedCategory === category
                            ? "bg-blue-100 text-blue-800 hover:bg-blue-200 shadow-sm"
                            : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                        }`}
                    >
                      <CategoryIcon category={category} className="w-4 h-4" />
                      {category}
                    </button>
                  ))}
                </div>

                {!showAllCategories && allCategories.length > featuredCategories.length && (
                  <div className="flex justify-center mt-4">
                    <button
                      onClick={() => {
                        setShowAllCategories(true)
                      }}
                      className="text-blue-600 hover:text-blue-700 font-medium flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      Show All Categories ({allCategories.length})
                    </button>
                  </div>
                )}
              </div>

              <div ref={taskResultsRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {isTaskSearching ? (
                  <div className="col-span-1 md:col-span-2 lg:col-span-3 flex flex-col items-center justify-center py-12">
                    <LoadingSpinner className="w-8 h-8 text-blue-600 mb-4" />
                    <p className="text-gray-600">Searching for tasks...</p>
                  </div>
                ) : displayedTasks.length > 0 ? (
                  <>
                    {displayedTasks.map((task, index) => (
                      <div
                        key={task.id}
                        data-task-id={task.id}
                        onClick={() => setSelectedTask(task)}
                        className={`group cursor-pointer rounded-xl p-6 transition-all duration-300 hover:shadow-xl ${
                          selectedTask?.id === task.id
                            ? `bg-gradient-to-br ${getTaskGradient(index)} transform scale-[1.05] shadow-lg`
                            : `bg-gradient-to-br ${getTaskGradient(index)} transform scale-[0.95] hover:scale-[1.02]`
                        }`}
                      >
                        <div className="flex items-start gap-4">
                          <span
                            className={`p-3 rounded-xl transition-all duration-300 ${
                              selectedTask?.id === task.id ? "bg-white/20" : "bg-white/25 backdrop-blur-sm"
                            }`}
                          >
                            <CategoryIcon
                              category={task.category || ""}
                              className={`w-6 h-6 ${
                                selectedTask?.id === task.id || !selectedTask ? "text-white" : "text-white"
                              }`}
                            />
                          </span>
                          <div className="flex-1">
                            <h3
                              className={`font-semibold text-lg mb-2 transition-colors duration-300 ${
                                selectedTask?.id === task.id ? "text-white" : "text-white group-hover:text-white"
                              }`}
                            >
                              {task.title}
                            </h3>
                            <p
                              className={`transition-colors duration-300 ${
                                selectedTask?.id === task.id ? "text-white/90" : "text-white/80 line-clamp-2"
                              }`}
                            >
                              {task.description}
                            </p>
                            {selectedTask?.id === task.id && (
                              <div className="mt-5 animate-fadeIn flex justify-end">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setCurrentStep("business-details")
                                  }}
                                  className="group flex items-center gap-2 px-6 py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 transition-all duration-300 font-medium shadow-md hover:shadow-lg transform hover:-translate-y-0.5 border border-white/30 relative"
                                >
                                  <div className="absolute inset-0 rounded-xl bg-white/10 animate-pulse opacity-70" />
                                  <span className="relative z-10">Get Started</span>
                                  <Play className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-300 relative z-10" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Always show custom task widget when search is performed */}
                    {activeSearchQuery.trim() !== "" && hasSearched && (
                      <div className="col-span-1 md:col-span-2 lg:col-span-3 mt-8">
                        <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl p-8 shadow-md">
                          <div className="flex flex-col items-center text-center space-y-6">
                            <div className="p-4 bg-white rounded-full shadow-sm">
                              <LightBulb className="w-10 h-10 text-blue-600" />
                            </div>
                            <div className="space-y-3">
                              <h3 className="text-2xl font-bold text-white">We Can Help With That!</h3>
                              <p className="text-lg text-white/90 max-w-2xl">
                                Let&apos;s help you out with &quot;
                                <span className="font-medium text-white">{activeSearchQuery}</span>&quot;—we&apos;ll
                                take care of everything for you.
                              </p>
                            </div>
                            <div className="pt-4">
                              <button
                                onClick={() => {
                                  // Use the active search query for creating the custom task
                                  const tempQuery = activeSearchQuery
                                  createDynamicTaskFromSearch(tempQuery)
                                }}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-blue-600 rounded-xl hover:bg-blue-50 transition-colors font-medium shadow-sm"
                              >
                                <Play className="w-5 h-5" />
                                Perform This Task
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : activeSearchQuery.trim() !== "" && hasSearched ? (
                  // Custom task section with positive messaging when no results
                  <div className="col-span-1 md:col-span-2 lg:col-span-3">
                    <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl p-8 shadow-md">
                      <div className="flex flex-col items-center text-center space-y-6">
                        <div className="p-4 bg-white rounded-full shadow-sm">
                          <LightBulb className="w-10 h-10 text-blue-600" />
                        </div>
                        <div className="space-y-3">
                          <h3 className="text-2xl font-bold text-white">We Can Help With That!</h3>
                          <p className="text-lg text-white/90 max-w-2xl">
                            Let&apos;s help you out with &quot;
                            <span className="font-medium text-white">{activeSearchQuery}</span>&quot;! This is exactly
                            the kind of call we&apos;re great at.
                          </p>
                        </div>
                        <div className="pt-4">
                          <button
                            onClick={() => {
                              // Use the active search query for creating the custom task
                              const tempQuery = activeSearchQuery
                              createDynamicTaskFromSearch(tempQuery)
                            }}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-blue-600 rounded-xl hover:bg-blue-50 transition-colors font-medium shadow-sm"
                          >
                            <Play className="w-5 h-5" />
                            Perform This Task
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : hasSearched ? (
                  // No results message when search has been performed
                  <div className="col-span-1 md:col-span-2 lg:col-span-3 text-center py-12">
                    <div className="inline-flex items-center justify-center p-4 bg-blue-50 rounded-full mb-4">
                      <Search className="w-8 h-8 text-blue-500" />
                    </div>
                    <h3 className="text-xl font-medium text-gray-900 mb-2">No tasks found</h3>
                    <p className="text-gray-600">
                      We couldn&apos;t find any tasks matching your search. Try different keywords or browse by
                      category.
                    </p>
                    <button
                      onClick={clearSearch}
                      className="mt-4 px-4 py-2 text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Clear Search
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="w-full space-y-6 md:space-y-8">
              {/* Task Details Card */}
              <div className="rounded-xl p-4 md:p-6 shadow-lg bg-gradient-to-br from-blue-500 to-purple-600 transform transition-all duration-300">
                <div className="flex items-start gap-4">
                  <span className="p-2 md:p-3 rounded-xl bg-white/20">
                    <CategoryIcon
                      category={selectedTask?.category || ""}
                      className="w-5 h-5 md:w-6 md:h-6 text-white"
                    />
                  </span>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="font-semibold text-base md:text-lg text-white">{selectedTask?.title}</h3>
                    </div>
                    <p className="text-sm md:text-base text-white/90">{selectedTask?.description}</p>
                  </div>
                </div>
              </div>

              {/* Business Details Form - No outer white card on mobile */}
              <div className="md:bg-white md:rounded-xl md:shadow-sm md:border md:border-gray-200 md:p-8 space-y-6 md:space-y-8">
                {/* Search Section */}
                <div className="bg-linear-to-br from-blue-50 to-indigo-50 p-4 md:p-6 rounded-xl border border-blue-100">
                  <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
                    <Search className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
                    <h2 className="text-lg md:text-xl font-semibold text-blue-900">Find a Business to Call</h2>
                  </div>

                  <p className="text-sm md:text-base text-blue-800 mb-4 md:mb-6">
                    Search for a business by name and location to automatically fill in their contact details.
                  </p>

                  <div className="space-y-4 md:space-y-6">
                    <div className="flex flex-col gap-3">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={businessSearchQuery}
                          onChange={(e) => setBusinessSearchQuery(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                          placeholder="Search for a business..."
                          className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-base md:text-lg"
                        />
                      </div>
                      <div className="w-full">
                        <div className="relative">
                          <Globe className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400" />
                          <input
                            type="text"
                            value={searchLocation}
                            onChange={(e) => setSearchLocation(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                            placeholder="Location (optional)"
                            className="w-full pl-10 md:pl-12 pr-3 md:pr-4 py-2.5 md:py-3 border border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-base md:text-lg"
                          />
                        </div>
                      </div>
                      <button
                        onClick={handleSearch}
                        disabled={isSearching}
                        className="min-w-[120px] px-6 py-2.5 md:py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSearching ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 md:h-5 md:w-5 border-b-2 border-white" />
                            <span>Searching...</span>
                          </>
                        ) : (
                          <>
                            <Search className="w-4 h-4 md:w-5 md:h-5" />
                            Search
                          </>
                        )}
                      </button>
                    </div>

                    {/* Search Results - Optimized for mobile */}
                    {searchResults && (
                      <div ref={searchResultsRef} className="mt-4 md:mt-8">
                        <h3 className="text-base md:text-lg font-medium text-gray-900 mb-3 md:mb-4">Search Results</h3>
                        <div className="grid gap-3 md:gap-4 md:grid-cols-2">
                          {searchResults.hits.map((hit) => (
                            <div
                              key={hit.document.id}
                              onClick={() => handleSearchResultClick(hit.document)}
                              className="max-w-full overflow-hidden bg-white rounded-xl p-3 md:p-4 border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer group"
                            >
                              <div className="flex items-start gap-2 md:gap-3">
                                <div className="shrink-0 p-1.5 md:p-2 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
                                  <Building className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                                </div>
                                <div className="flex-1 min-w-0 space-y-0.5 md:space-y-1">
                                  <h4 className="font-medium text-sm md:text-base text-gray-900 break-words line-clamp-2">
                                    {hit.document.name}
                                  </h4>
                                  {hit.document.phone && (
                                    <p className="text-xs md:text-sm text-gray-600 flex items-center gap-1.5">
                                      <Phone className="w-3 h-3 md:w-4 md:h-4 shrink-0" />
                                      <span className="truncate">
                                        {formatAnyPhoneNumber(hit.document.countryCode || "1", hit.document.phone)}
                                      </span>
                                    </p>
                                  )}
                                  <p className="text-xs md:text-sm text-gray-600 flex items-center gap-1.5">
                                    <Globe className="w-3 h-3 md:w-4 md:h-4 shrink-0" />
                                    <span className="truncate">
                                      {formatAddress(hit.document.address, hit.document.city, hit.document.state)}
                                    </span>
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Manual Input Section */}
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="px-3 bg-white text-xs md:text-sm text-gray-500">or enter details manually</span>
                  </div>
                </div>

                {/* Manual Form - Simplified on mobile */}
                <div className="space-y-4 md:space-y-6 bg-gray-50 p-4 md:p-6 rounded-xl">
                  <div>
                    <label htmlFor="businessName" className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                      Business Name
                    </label>
                    <input
                      type="text"
                      id="businessName"
                      value={businessDetails.name}
                      onChange={(e) => {
                        setBusinessDetails((prev) => ({ ...prev, name: e.target.value }))
                        validateBusinessName(e.target.value)
                      }}
                      className={`w-full px-3 md:px-4 py-2.5 md:py-3 rounded-xl border ${
                        businessNameError ? "border-red-500" : "border-gray-200"
                      } focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base md:text-base bg-white`}
                      placeholder="Enter the business name"
                      required
                    />
                    {businessNameError && <p className="text-xs md:text-sm text-red-600 mt-1">{businessNameError}</p>}
                  </div>
                  <div>
                    <label htmlFor="phoneNumber" className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <div className="flex flex-col gap-2">
                      <select
                        id="countryCode"
                        value={businessDetails.countryCode}
                        onChange={(e) => {
                          setBusinessDetails((prev) => ({ ...prev, countryCode: e.target.value }))
                          validatePhoneNumber(businessDetails.phoneNumber, e.target.value)
                        }}
                        className="w-full px-2 md:px-4 py-2.5 md:py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base md:text-base bg-white mb-2 sm:mb-0"
                      >
                        {supportedCountryCodes.map((country) => (
                          <option key={country} value={countryCodeInfo[country].code}>
                            {countryCodeInfo[country].displayName}
                          </option>
                        ))}
                      </select>
                      <input
                        type="tel"
                        id="phoneNumber"
                        ref={phoneNumberInputRef}
                        value={businessDetails.phoneNumber}
                        onChange={(e) => {
                          setBusinessDetails((prev) => ({ ...prev, phoneNumber: e.target.value }))
                          validatePhoneNumber(e.target.value, businessDetails.countryCode)
                        }}
                        className={`w-full px-3 md:px-4 py-2.5 md:py-3 rounded-xl border ${
                          phoneError ? "border-red-500" : "border-gray-200"
                        } focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base md:text-base bg-white`}
                        placeholder="(555) 123-4567"
                        pattern="^\(?\d{1,3}\)?([\s\-]?\d{1,4}){1,3}$"
                        inputMode="tel"
                        required
                      />
                    </div>
                    {phoneError && <p className="text-xs md:text-sm text-red-600">{phoneError}</p>}
                  </div>
                </div>

                {/* Action Buttons - moved from footer */}
                {selectedTask && currentStep === "business-details" && (
                  <div className="flex flex-col md:flex-row md:justify-end gap-3 pt-4">
                    <button
                      onClick={() => {
                        setCurrentStep("select-task")
                      }}
                      className="min-w-[120px] px-6 py-2.5 md:py-3 bg-white text-blue-600 rounded-xl border border-blue-600 hover:bg-blue-50 transition-colors font-medium flex items-center justify-center gap-2"
                    >
                      Back
                    </button>
                    <button
                      onClick={() => {
                        // Validate both fields before proceeding
                        const isBusinessNameValid = validateBusinessName(businessDetails.name)
                        const isPhoneNumberValid = validatePhoneNumber(
                          businessDetails.phoneNumber,
                          businessDetails.countryCode
                        )

                        // If either validation fails, return early
                        if (!isBusinessNameValid || !isPhoneNumberValid) {
                          console.log("Validation failed, returning", {
                            isBusinessNameValid,
                            isPhoneNumberValid
                          })
                          return
                        }

                        const sanitizedPhoneNumber = businessDetails.phoneNumber.replace(/\D/g, "")
                        const sanitizedCountryCode = businessDetails.countryCode.replace(/\D/g, "")
                        const prompt = selectedTask.generatePrompt(
                          `Business Name: ${businessDetails.name}\nCountry Code: ${sanitizedCountryCode}\nPhone Number: ${sanitizedPhoneNumber}`
                        )

                        const base64Prompt = encodeURIComponent(Buffer.from(prompt, "utf-8").toString("base64"))
                        navigate(`/calls/from-dynamic-task?task=${base64Prompt}`)
                      }}
                      className="min-w-[120px] px-6 py-2.5 md:py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2"
                    >
                      Configure Call
                      <Play className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
